import { Breadcrumbs, Layout, Loader } from '@components/common'
import AuxSidebar from '@components/common/AuxSidebar'
import MobileAuxSidebar from '@components/common/MobileAuxSidebar'
import { Plus } from '@components/icons'
import { useUI } from '@components/ui'
import SlideInSidebar from '@components/ui/SlideInSidebar'
import commerce from '@lib/api/commerce'
import getGlobalStaticProps from '@lib/get-global-static-props'
import { createClient } from '@lib/prismicio'
import { SliceZone } from '@prismicio/react'
import { AnalyticsPageType } from '@shopify/hydrogen-react'
import cn from 'clsx'
import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { components } from 'slices'

export async function getStaticProps({
  params,
  locale,
  previewData,
}: GetStaticPropsContext<{ slug: string }>) {
  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  const page = await client.getByUID('help-page', params!.slug)
  const productInfoIndex = await client.getByUID('help-page', 'product')

  if (!page) {
    return {
      notFound: true,
    }
  }

  const auxPageNav = await client.getByUID(
    'section-side-navigation',
    'product-information-side-navigation'
  )

  return {
    props: {
      ...globalProps,
      page,
      auxPageNav: auxPageNav?.data || null,
      productInfoIndex: productInfoIndex?.data || null,
      analyticsPayload: {
        pageType: AnalyticsPageType.page,
      },
    },
    revalidate: 60 * 60, // Every hour
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  return {
    paths: ['/product-information/lab-created-diamonds'],
    fallback: 'blocking',
  }
}

export default function Slug({
  page,
  auxPageNav,
  productInfoIndex,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const {
    Title: productInfoIndexTitle,
    description: productInfoIndexDescription,
  }: any = productInfoIndex || ''

  const router = useRouter()
  const [showMobileNav, setShowMobileNav] = useState(false)
  const [isMenuSticky, setIsMenuSticky] = useState(false)
  const menuTriggerRef = useRef() as React.MutableRefObject<HTMLDivElement>
  const { openHelpMenu, closeHelpMenu } = useUI()

  const { data } = page || {}
  const { fullWidth, Title, description, slices } = data || {}
  const { Links }: any = auxPageNav || []

  const handleScroll = () => {
    if (menuTriggerRef && menuTriggerRef?.current) {
      setIsMenuSticky(menuTriggerRef.current.getBoundingClientRect().top <= 60)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', () => handleScroll)
    }
  }, [])

  const handleClosMobileNav = () => {
    setShowMobileNav(false)
    closeHelpMenu()
  }
  const handleShowMobileNav = () => {
    setShowMobileNav(true)
    openHelpMenu()
  }

  useEffect(() => {
    const handleRouteChange = () => {
      setShowMobileNav(false)
      closeHelpMenu()
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events, closeHelpMenu])

  if (router.isFallback) {
    return <Loader title="general-page-loader" />
  }

  return (
    <div className="wrapper fit">
      <Breadcrumbs current={Title} />
      <div className="mt-12 pb-20">
        {!fullWidth ? (
          <>
            <div className="text-brand-dark-grey w-full lg:w-160 pb-20 mx-auto">
              <h2 className="text-h4 pb-4 text-center font-bold">
                {productInfoIndexTitle}
              </h2>
              <div className="body1 text-center">
                {productInfoIndexDescription}
              </div>
            </div>
            <div className="w-full">
              <div className="block lg:flex flex-column lg:flex-row gap-x-44 gap-y-24">
                <div className="hidden lg:flex w-full lg:w-76">
                  <AuxSidebar auxItems={Links} />
                </div>
                <div className="sticky top-16 z-30 block lg:hidden pb-10">
                  <div
                    className={cn('transition-all duration-150 ', {
                      'fixed-component-shadow': isMenuSticky,
                    })}
                    ref={menuTriggerRef}
                  >
                    <button
                      onClick={handleShowMobileNav}
                      className="flex justify-between py-4 pl-4 w-full  lg:hidden focus:outline-none pr-4 bg-brand-light-grey items-center"
                    >
                      <div className="body-1-bold text-brand-dark-grey">
                        {Links[0].linkTitle}
                      </div>
                      <Plus />
                    </button>
                  </div>
                </div>
                <div className="block lg:hidden">
                  {Links && Links?.length > 0 && (
                    <SlideInSidebar
                      display={showMobileNav}
                      closeButton={false}
                      onClose={handleClosMobileNav}
                      orientation="bottom"
                      className={cn('h-auto rounded-t-lg', {
                        'translate-y-0': showMobileNav,
                        'translate-y-full': !showMobileNav,
                      })}
                    >
                      <MobileAuxSidebar
                        menuItems={Links}
                        onClose={handleClosMobileNav}
                        title="Help"
                      />
                    </SlideInSidebar>
                  )}
                </div>
                <div className="w-full lg:w-4/5">
                  <SliceZone slices={slices} components={components} />
                </div>
              </div>
            </div>
          </>
        ) : (
          <SliceZone slices={slices} components={components} />
        )}
      </div>
    </div>
  )
}

Slug.Layout = Layout
